import { useEffect, useState } from "react";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import { Box, Grid2, Link, Stack, Typography, useTheme, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { useTranslation } from "react-i18next";
import { createAttachment, catalogByPart } from "../../../../../apis";
import { returnIsValid, useConstants } from "../Main/useConstants";
import { EGSwitch } from "../../../../MainComponents/Switch/Switch";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import {
  InvestigationButton,
  InvestigationItemContainer,
  Separator,
  TemporalUploadedFile,
  InvestigationCollapsable,
} from "../components";
import { RichTextEditor } from "../../../../MainComponents/RichTextEditor/RichTextEditor";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { AddFile } from "./Modals/AddFile";
import { AddMeasure } from "./Modals/AddMeasure";
import { Conclude } from "./Modals/Conclude";
import EGDateTImePicker from "../../../../MainComponents/DatePicker/EGDateTImePicker";
import {
  parseConclusion,
  buildConclusionString,
  initializeReportConclusions,
} from "../helpers/conclusionParser";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
const validateForm = (conclusion, mainEvidence) => {
  const errors = {};

  const text = conclusion.replace(/<[^>]*>/g, "").trim();
  if (text.length === 0) {
    errors.conclusion = true;
  }

  if (!mainEvidence || mainEvidence.trim() === "") {
    errors.main_evidence = true;
  }

  return [Object.entries(errors).length === 0 ? true : false, errors];
};

export const AddConclusion = ({
  conclusion,
  // setSelectedConclusion,
  setIsEditing,
  handleSaveConclusionDetails,
  isLoading,
  setIsLoading,
  assignsDetails = [],
  onReloadReport,
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [richTextEditor, setRichTextEditor] = useState("");
  const [comment, setComment] = useState("");
  const [date, setDate] = useState(null);
  // Files from DB
  const [uploadedFiles, setUploadedFiles] = useState([]);
  // Local files
  const [files, setFiles] = useState([]);

  const [measures, setMeasures] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);
  const [errors, setErrors] = useState({});

  const [showAddConclusionFiles, setShowAddConclusionFiles] = useState(false);
  const [showAddConclusionMeasures, setShowAddConclusionMeasures] =
    useState(false);
  const [conclusionDate, setConclusionDate] = useState(false);
  const [addAdditionalObservations, setAddAdditionalObservations] =
    useState(false);
  const [selectedConclusionMeasure, setSelectedConclusionMeasure] =
    useState(null);

  const [showConclude, setShowConclude] = useState(false);

  // Main Evidence catalog
  const [mainEvidenceOpts, setMainEvidenceOpts] = useState([]);
  const [mainEvidence, setMainEvidence] = useState("");

  // Conclusiones por reporte (solo cuando hay más de 1 denuncia)
  const [reportConclusions, setReportConclusions] = useState([]);
  const hasMultipleReports = assignsDetails.length > 1;

  // Cargar catálogo de tipos de evidencia principal
  useEffect(() => {
    const loadMainEvidenceCatalog = async () => {
      try {
        const resp = await catalogByPart({
          is_own: true,
          catalogue: "mainEvidenceInTheConclusion",
          path: "/",
        });

        console.log("Main Evidence Catalog Response:", resp);

        if (resp?.data && Array.isArray(resp.data)) {
          const formattedOptions = resp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          console.log("Formatted Main Evidence Options:", formattedOptions);
          setMainEvidenceOpts(formattedOptions);
        } else {
          console.warn("Main Evidence catalog returned no data or invalid format");
          setMainEvidenceOpts([]);
        }
      } catch (error) {
        console.error("Error loading main evidence catalog:", error);
        setMainEvidenceOpts([]);
      }
    };

    loadMainEvidenceCatalog();
  }, []);

  useEffect(() => {
    if (conclusion !== null) {
      let date = dayjs(conclusion.conclusion_at);

      // Parsear la conclusión para separar general de conclusiones por reporte
      const { generalConclusion, reportConclusions: parsedReportConclusions } =
        parseConclusion(conclusion.conclusion);

      setRichTextEditor(generalConclusion);
      setConclusionDate(!!conclusion.conclusion_at);
      setAddAdditionalObservations(!!conclusion.comment);
      setComment(conclusion.comment);
      setDate(date.isValid() ? date : null);
      setUploadedFiles(conclusion.files);
      setMeasures(conclusion.measures);
      setMainEvidence(conclusion.main_evidence || "");

      // Inicializar conclusiones por reporte
      if (hasMultipleReports) {
        const initializedConclusions = initializeReportConclusions(
          assignsDetails,
          parsedReportConclusions
        );
        setReportConclusions(initializedConclusions);
      }
    } else {
      setRichTextEditor("");
      setComment("");
      setDate(null);
      setFiles([]);
      setMeasures([]);
      setMainEvidence("");

      // Inicializar conclusiones por reporte vacías
      if (hasMultipleReports) {
        const initializedConclusions = initializeReportConclusions(assignsDetails, []);
        setReportConclusions(initializedConclusions);
      }
    }
  }, [conclusion, assignsDetails]);

  // Validar cada que se hace un cambio
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm(richTextEditor, mainEvidence);
      setErrors(errors);
    }
  }, [richTextEditor, mainEvidence]);

  // End of edition
  const handleClose = () => {
    setIsEditing(false);
  };

  // Send files to DB
  const sendFiles = async () => {
    let uploadedAttachments = [];

    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("attachments", file);
      });

      try {
        const resp = await createAttachment(formData);
        const attachments_id = resp.attachments_id;
        uploadedAttachments = attachments_id.map((id, idx) => ({
          file: id,
          name: files[idx].name,
          details: files[idx].details || "",
          size: files[idx].size,
          type: files[idx].type,
        }));
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }

    const existingFiles = uploadedFiles.map((file) => ({
      details: file.details || "",
      file: file.file, // id file
      name: file.name,
      size: file.size,
      type: file.type,
      url: file.url,
    }));

    // Merging new and existing files
    return [...existingFiles, ...uploadedAttachments];
  };

  const handleSave = async (conclude = false) => {
    setIsLoading(true);

    const [isValid, errors] = validateForm(richTextEditor, mainEvidence);

    setErrors(errors);
    setTriedToSave(true);

    if (!isValid) {
      setShowConclude(false);
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      setIsLoading(false);
      return;
    }

    try {
      const files = await sendFiles();
      setFiles(files);
      setUploadedFiles([]);

      // Construir el string de conclusión combinado (general + por reporte)
      const combinedConclusion = buildConclusionString(
        richTextEditor,
        hasMultipleReports ? reportConclusions : []
      );

      await handleSaveConclusionDetails({
        conclusion: combinedConclusion,
        comment: comment ?? null,
        conclusion_at: date || null,
        folder: {
          main_evidence: mainEvidence || null,
          conclusion: {
            files,
            measures,
          },
        },
      });
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Actualizar conclusión de un reporte específico
  const handleUpdateReportConclusion = (reportId, newConclusion) => {
    setReportConclusions((prev) =>
      prev.map((rc) =>
        rc.report_id === reportId ? { ...rc, conclusion: newConclusion } : rc
      )
    );
  };

  const handleDeleteUploadedFile = (idx) => {
    const newUploadedFiles = _.cloneDeep(uploadedFiles);
    newUploadedFiles.splice(idx, 1);
    setUploadedFiles(newUploadedFiles);
  };

  const handleDeleteTemporalFile = (idx) => {
    const newFiles = _.cloneDeep(files);
    newFiles.splice(idx, 1);
    setFiles(newFiles);
  };

  const handleEditConclusionMeasure = (idx) => {
    setSelectedConclusionMeasure(idx);
    setShowAddConclusionMeasures(true);
  };

  const handleDeleteConclusionMeasure = (idx) => {
    const newConclusionMeasures = _.cloneDeep(measures);
    newConclusionMeasures.splice(idx, 1);
    setMeasures(newConclusionMeasures);
  };

  // Función para mostrar el modal de concluir después de validar
  const handleShowConcludeModal = () => {
    const [isValid, validationErrors] = validateForm(richTextEditor, mainEvidence);
    setErrors(validationErrors);
    setTriedToSave(true);

    if (!isValid) {
      enqueueSnackbar({
        variant: "error",
        message: t("resources.missing_required_inputs"),
      });
      return;
    }

    setShowConclude(true);
  };

  // Construir la conclusión actual del formulario para el modal
  const buildCurrentConclusion = () => {
    const combinedConclusion = buildConclusionString(
      richTextEditor,
      hasMultipleReports ? reportConclusions : []
    );

    return {
      conclusion: combinedConclusion,
      conclusion_at: date,
      comment: comment ?? "",
      files: uploadedFiles,
      measures: measures,
      main_evidence: mainEvidence || "",
    };
  };

  // Función para guardar y concluir desde el modal
  const handleSaveAndConclude = async (conclusionData) => {
    setIsLoading(true);

    try {
      const filesResult = await sendFiles();
      setFiles(filesResult);
      setUploadedFiles([]);

      const combinedConclusion = buildConclusionString(
        richTextEditor,
        hasMultipleReports ? reportConclusions : []
      );

      await handleSaveConclusionDetails({
        conclusion: combinedConclusion,
        comment: comment ?? null,
        conclusion_at: conclusionData.conclusion_at || null,
        folder: {
          main_evidence: mainEvidence || null,
          conclusion: {
            files: filesResult,
            measures,
          },
        },
      });

      handleClose();
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Conclude
        show={showConclude}
        onHide={() => setShowConclude(false)}
        handleSaveConclusionDetails={handleSaveAndConclude}
        conclusion={buildCurrentConclusion()}
        assignsDetails={assignsDetails}
        onReloadReport={onReloadReport}
      />
      <AddFile
        show={showAddConclusionFiles}
        onHide={() => setShowAddConclusionFiles(false)}
        files={files}
        setFiles={setFiles}
      />
      <AddMeasure
        show={showAddConclusionMeasures}
        onHide={() => setShowAddConclusionMeasures(false)}
        measures={measures}
        setMeasures={setMeasures}
        selectedConclusionMeasure={selectedConclusionMeasure}
        setSelectedConclusionMeasure={setSelectedConclusionMeasure}
      />
      <Box padding={2}>
        <Separator
          text={
            conclusion !== null
              ? t("Conclusion.edit_conclusion")
              : t("Conclusion.add_conclusion")
          }
        />

        <Grid2 container spacing={2} mt={2}>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display="flex"
            alignItems="center"
            justifyContent="space-around"
          >
            <Typography
              fontSize={16}
              fontWeight={600}
              color={theme.palette.secondary.main}
            >
              {t("Conclusion.set_conclusion_date")}
            </Typography>
            <EGSwitch
              size="small"
              checked={conclusionDate}
              onChange={(e) => setConclusionDate(e.target.checked)}
            />
          </Grid2>

          <Grid2
            size={{ xs: 12, lg: 6 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={1}
          >
            {conclusionDate && (
              <>
                <EGDateTImePicker
                  label={`${t("Conclusion.date")}:`}
                  value={date}
                  onChange={setDate}
                  format="DD/MMM/YYYY hh:mm A"
                />
              </>
            )}
          </Grid2>

          <Grid2 size={12}>
            <RichTextEditor
              label={t("Conclusion.investigation_conclusion")}
              value={richTextEditor}
              onChange={(value) => setRichTextEditor(value)}
              isValid={returnIsValid(errors?.conclusion, triedToSave)}
            />
          </Grid2>

          <Grid2 size={12}>
            <EGBasicSelect
              label={t("Conclusion.main_evidence_type")}
              name="main_evidence"
              value={mainEvidence}
              onChange={(e) => setMainEvidence(e.target.value)}
              options={mainEvidenceOpts}
              showHelpText={false}
              required
              isValid={returnIsValid(errors?.main_evidence, triedToSave)}
              showSuccessValidation={triedToSave && mainEvidence}
            />
          </Grid2>

          {/* Sección de conclusiones por denuncia - solo si hay más de 1 reporte */}
          {hasMultipleReports && (
            <Grid2 size={12}>
              <Separator
                text={t("Conclusion.conclusions_by_report")}
                sx={{ mb: 2 }}
              />
              <Stack spacing={1}>
                {reportConclusions.map((rc) => (
                  <Accordion
                    key={rc.report_id}
                    sx={{
                      border: `1px solid ${theme.palette.blueGray200.main}`,
                      borderRadius: "8px !important",
                      "&:before": { display: "none" },
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<Icon name="arrow_down_2" size={20} />}
                      sx={{
                        backgroundColor: theme.palette.blueGray50.main,
                        borderRadius: "8px",
                        minHeight: "48px",
                        "&.Mui-expanded": {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={600} color="primary">
                          {rc.folio}
                        </Typography>
                        <Typography color="secondary" fontSize={14}>
                          | {rc.classification_label}
                        </Typography>
                        {rc.conclusion && rc.conclusion.replace(/<[^>]*>/g, "").trim() !== "" && (
                          <Typography
                            component="span"
                            sx={{
                              backgroundColor: theme.palette.green.main,
                              color: "white",
                              fontSize: 10,
                              px: 1,
                              py: 0.25,
                              borderRadius: "4px",
                            }}
                          >
                            {t("Conclusion.has_conclusion")}
                          </Typography>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2 }}>
                      <RichTextEditor
                        label={`${t("Conclusion.conclusion_for")} ${rc.folio}`}
                        value={rc.conclusion}
                        onChange={(value) =>
                          handleUpdateReportConclusion(rc.report_id, value)
                        }
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Grid2>
          )}

          <Grid2 size={12}>
            {addAdditionalObservations ? (
              <Box>
                <EGInput
                  label={`${t(
                    "Conclusion.additional_observations"
                  )}`}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  type="textarea"
                  multiline
                  rows={5}
                />
              </Box>
            ) : (
              <Box display="flex" justifyContent="flex-end" mt={-1}>
                <Link
                  onClick={() => setAddAdditionalObservations(true)}
                  underline="hover"
                >
                  + {t("Conclusion.add_additional_observations")}
                </Link>
              </Box>
            )}
          </Grid2>
        </Grid2>

        <Stack
          spacing={2}
          mt={2}
          p={2}
          borderRadius="5px"
          border={`1px solid ${theme.palette.primary.main}`}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <InvestigationItemContainer
              color={theme.palette.primary.main}
              width="fit-content"
              sx={{ padding: 0.8 }}
            >
              <Typography fontSize={13} fontWeight={600}>
                {t("Conclusion.actions_of_conclusion")}
              </Typography>
            </InvestigationItemContainer>

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="flex-end"
              spacing={1}
            >
              <InvestigationButton
                label={`${t("Conclusion.upload_files")}`}
                icon={false}
                size="medium"
                onClick={() => setShowAddConclusionFiles(true)}
              />
              <InvestigationButton
                label={`${t("Conclusion.measures_corrective")}`}
                size="medium"
                icon={false}
                onClick={() => setShowAddConclusionMeasures(true)}
              />
            </Stack>
          </Stack>

          {(uploadedFiles.length > 0 ||
            measures.length > 0 ||
            files.length > 0) && <hr style={{ margin: 0 }} />}

          {(uploadedFiles.length > 0 || files.length > 0) && (
            <>
              <Separator text={t("Conclusion.support_files_description_for_conclusion")} />
              <Grid2 container spacing={1}>
                {uploadedFiles.map((file, idxFile) => (
                  <Grid2
                    size={{ xs: 12, md: 6 }}
                    key={`uploaded-file-${idxFile}`}
                  >
                    <TemporalUploadedFile
                      name={file.name}
                      description={file.details}
                      size={file.size}
                      iconName="trash_can"
                      iconAction={() => handleDeleteUploadedFile(idxFile)}
                      iconColor={theme.palette.red.main}
                      width="100%"
                    />
                  </Grid2>
                ))}
                {files.map((file, idxFile) => (
                  <Grid2
                    size={{ xs: 12, md: 6 }}
                    key={`temporal-file-${idxFile}`}
                  >
                    <TemporalUploadedFile
                      name={file.name}
                      description={file.details}
                      size={file.size}
                      iconName="trash_can"
                      iconAction={() => handleDeleteTemporalFile(idxFile)}
                      iconColor={theme.palette.red.main}
                      width="100%"
                    />
                  </Grid2>
                ))}
              </Grid2>
            </>
          )}

          {measures.length > 0 && (
            <>
              <Separator
                text={t(
                  "Conclusion.measures_corrective_description"
                )}
              />
              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;

                  const items = Array.from(measures);
                  const [reorderedItem] = items.splice(result.source.index, 1);
                  items.splice(result.destination.index, 0, reorderedItem);

                  setMeasures(items);
                }}
              >
                <Droppable droppableId="conclusionMeasures">
                  {(provided) => (
                    <Stack {...provided.droppableProps} ref={provided.innerRef}>
                      {measures.map((conclusionMeasure, idx) => (
                        <Draggable
                          key={idx}
                          draggableId={`conclusionMeasure-${idx}`}
                          index={idx}
                        >
                          {(provided, snapshot) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.8 : 1,
                              }}
                              sx={{ my: 0.5 }}
                            >
                              <InvestigationCollapsable
                                title={
                                  <Typography
                                    fontSize={14}
                                    fontWeight={600}
                                    color={theme.palette.blueGray600.main}
                                  >
                                    {conclusionMeasure.measure}
                                  </Typography>
                                }
                                details={
                                  conclusionMeasure.details && (
                                    <>
                                      <Typography
                                        fontSize={14}
                                        fontWeight={600}
                                        color={theme.palette.blueGray600.main}
                                      >
                                        {t("Conclusion.details")}:
                                        <Typography
                                          component="span"
                                          fontSize={12}
                                          fontWeight={400}
                                          color={theme.palette.oxfordBlue.main}
                                          fontStyle="italic"
                                        >
                                          {conclusionMeasure.details}
                                        </Typography>
                                      </Typography>
                                    </>
                                  )
                                }
                                labelCollapse={t("Conclusion.details")}
                                dragHandleProps={provided.dragHandleProps}
                                buttons={[
                                  {
                                    tooltip: t("Conclusion.delete"),
                                    name: "trash_can",
                                    color: theme.palette.red.main,
                                    onClick: () =>
                                      handleDeleteConclusionMeasure(idx),
                                  },
                                  {
                                    tooltip: t("Conclusion.edit"),
                                    name: "outline_edit",
                                    color: theme.palette.primary.main,
                                    onClick: () =>
                                      handleEditConclusionMeasure(idx),
                                  },
                                ]}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="flex-end"
          spacing={1}
          mt={2}
        >
          <InvestigationButton
            label={`${t("Conclusion.cancel")}`}
            icon="close_icon"
            size="medium"
            onClick={handleClose}
            loading={isLoading}
          />
          <InvestigationButton
            label={`${t("Conclusion.save")}`}
            variant="success"
            size="medium"
            icon="mark_check_bold"
            onClick={() => handleSave()}
            loading={isLoading}
          />
          <InvestigationButton
            label={`${t("Conclusion.save_and_conclude")}`}
            variant="success"
            size="medium"
            icon="mark_check_bold"
            onClick={() => handleShowConcludeModal()}
            loading={isLoading}
          />
        </Stack>
      </Box>
    </>
  );
};
